import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DOSSIER_SCENARIO_LABELS, DossierScenarioSchema } from '../../lib/api-types';
import type { DossierCreateInput } from '../../lib/api';
import { useMandants } from '../mandants/hooks';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { FieldHelp } from '../../components/ui/FieldHelp';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';

const createSchema = z.object({
  mandantId: z.string().min(1, 'Veuillez choisir un mandant.'),
  scenario: DossierScenarioSchema,
  objet: z.string().optional(),
});
type CreateValues = z.infer<typeof createSchema>;

/** Clearly fictitious demo objet (a sample label, never an official value). */
const DEMO_OBJET = 'Évaluation — Villa R+1, Ouagadougou — Ouaga 2000 (ex. démonstration)';

export function DossierCreateForm({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (input: DossierCreateInput) => void;
  onCancel: () => void;
  pending?: boolean;
}): JSX.Element {
  // Only active mandants can own a new dossier.
  const mandantsQuery = useMandants({ page: 1, pageSize: 100 });
  const mandants = useMemo(() => mandantsQuery.data?.items ?? [], [mandantsQuery.data]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { mandantId: '', scenario: 'PV_RAPPORT', objet: '' },
  });

  const [confirmDemo, setConfirmDemo] = useState(false);

  const applyDemo = () => {
    // Prefill coherent demo values client-side; the user can edit them after.
    const current = getValues();
    setValue('mandantId', current.mandantId || mandants[0]?.id || '', { shouldValidate: false });
    setValue('scenario', 'PV_RAPPORT');
    setValue('objet', DEMO_OBJET);
  };

  const requestDemo = () => {
    const values = getValues();
    const hasData =
      Boolean(values.mandantId) ||
      (values.objet ?? '').trim().length > 0 ||
      values.scenario !== 'PV_RAPPORT';
    if (hasData) {
      setConfirmDemo(true);
      return;
    }
    applyDemo();
  };

  const submit = handleSubmit((values) => {
    onSubmit({
      mandantId: values.mandantId,
      scenario: values.scenario,
      objet: values.objet ? values.objet : undefined,
    });
  });

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {mandantsQuery.isError ? (
        <Alert variant="error">Impossible de charger la liste des mandants.</Alert>
      ) : null}

      <div className="rounded-md border border-dashed border-brand-200 bg-brand-50/50 px-3 py-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={requestDemo}
          disabled={pending || mandantsQuery.isLoading}
        >
          Utiliser un exemple de démonstration
        </Button>
        <FieldHelp className="mt-1">
          Remplit le formulaire avec des valeurs d’exemple, que vous pouvez ensuite modifier.
        </FieldHelp>
      </div>

      <div>
        <Label htmlFor="mandantId">Mandant</Label>
        <Select id="mandantId" aria-invalid={Boolean(errors.mandantId)} {...register('mandantId')}>
          <option value="">— Choisir un mandant —</option>
          {mandants.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nom}
            </option>
          ))}
        </Select>
        {errors.mandantId ? (
          <p className="mt-1 text-sm text-red-600">{errors.mandantId.message}</p>
        ) : null}
        <FieldHelp>
          Le mandant est le client qui commande l’évaluation (banque, assurance, particulier…).
        </FieldHelp>
      </div>

      <div>
        <Label htmlFor="scenario">Scénario</Label>
        <Select id="scenario" {...register('scenario')}>
          {DossierScenarioSchema.options.map((s) => (
            <option key={s} value={s}>
              {DOSSIER_SCENARIO_LABELS[s]}
            </option>
          ))}
        </Select>
        <FieldHelp>
          Les livrables attendus : un procès-verbal (PV), un rapport, ou les deux.
        </FieldHelp>
      </div>

      <div>
        <Label htmlFor="objet">Objet (facultatif)</Label>
        <Input
          id="objet"
          placeholder="ex. : Évaluation d’une villa à usage résidentiel"
          {...register('objet')}
        />
        <FieldHelp example="Évaluation — Villa R+1, Ouagadougou — Ouaga 2000">
          Une courte description libre de la mission, utile pour retrouver le dossier.
        </FieldHelp>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Annuler
        </Button>
        <Button type="submit" loading={pending}>
          Créer le dossier
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDemo}
        onClose={() => setConfirmDemo(false)}
        onConfirm={() => {
          applyDemo();
          setConfirmDemo(false);
        }}
        title="Remplacer par l’exemple de démonstration ?"
        description="Des informations ont déjà été saisies. Voulez-vous les remplacer par les valeurs d’exemple ? Vous pourrez encore les modifier ensuite."
        confirmLabel="Remplacer"
      />
    </form>
  );
}
